<script lang="ts">
	import { recaptchaConfig } from '@helpers/settings'
	import { createEventDispatcher, onMount } from 'svelte'

	interface Props {
		loading: boolean
	}

	let { loading = $bindable() }: Props = $props()

	const key = recaptchaConfig.siteKey

	const dispatch = createEventDispatcher()

	onMount(() => {
		// Copied from https://developers.google.com/recaptcha/docs/loading#loading_recaptcha_asynchronously

		// How this code snippet works:
		// This logic overwrites the default behavior of `grecaptcha.ready()` to
		// ensure that it can be safely called at any time. When `grecaptcha.ready()`
		// is called before reCAPTCHA is loaded, the callback function that is passed
		// by `grecaptcha.ready()` is enqueued for execution after reCAPTCHA is
		// loaded.
		if (typeof window['grecaptcha'] === 'undefined') {
			window['grecaptcha'] = {}
		}
		window['grecaptcha'].ready = (cb: undefined | Function) => {
			if (typeof window['grecaptcha'] === 'undefined') {
				// window.__grecaptcha_cfg is a global variable that stores reCAPTCHA's
				// configuration. By default, any functions listed in its 'fns' property
				// are automatically executed when reCAPTCHA loads.
				const c = '___grecaptcha_cfg'
				window[c] = window[c] || {}
				;(window[c]['fns'] = window[c]['fns'] || []).push(cb)
			} else {
				cb()
			}
		}
	})

	export function handleVerify() {
		loading = true

		if (recaptchaConfig.token) {
			// skip reCAPTCHA
			dispatch('success', recaptchaConfig.token)
		} else {
			window['grecaptcha'].ready(() => {
				try {
					window['grecaptcha'].execute(key, { action: 'submit' }).then((recaptchaToken: string) => {
						dispatch('success', recaptchaToken)
					})
				} catch (e) {
					dispatch('error', e)
				}
			})
		}

		loading = false
	}
</script>

<svelte:head>
	<script async defer src="https://www.google.com/recaptcha/api.js?render={key}"></script>
</svelte:head>

<style>
	:global(.grecaptcha-badge) {
		bottom: 104px !important; /* 104 px = Intercom height (60) + spacing (2 * 20) + OCD spacing (4) */
	}

	:global(.is-logged .grecaptcha-badge) {
		display: none !important;
	}
</style>
